import { InjectionToken, Provider } from '@angular/core';

import { MibMoment, mibMs, MibPalette, MibTheme } from '../../foundations';
import { MibIconName } from '../icons';
import { MibThumbnailUnavailable } from '../thumbnail';
import { durationToTime } from '../../utils/time.utils';

/**
 * One case, as a page holds it. Only the title is required, and a card given it alone is a correct
 * card that keeps its shape.
 *
 * It carries **what the card draws itself**: the picture and what is laid over it. Everything written
 * under the title — a procedure name, who and when, counts, tags — is the page's, and goes in the two
 * slots the card opens for it. A product's facts keep growing, and a field per fact is a race the
 * library loses.
 */
export interface MibCase {
  readonly title: string;

  /** Where the case opens, a real URL: the card is a link. Without it the title is plain text and nothing opens. */
  readonly href?: string;

  readonly thumbnail?: string;

  /**
   * What to say while the case is not ready to watch — uploading, encoding, being analysed.
   *
   * Given, a spinner is drawn in the middle of the picture with this sentence under it, over the
   * thumbnail if there is one and on the bare ground if there is not. It is the picture that is not
   * ready, so it is the picture that says so rather than a line under the title.
   */
  readonly preparing?: string;

  /**
   * The picture is not available here. Given, the thumbnail is not drawn: a plain ground holds the
   * icon in the middle with the label written under it.
   */
  readonly unavailable?: MibCaseUnavailable;

  /** How long the case runs, in seconds. Written as `22:48` or `1:02:35` in the picture's corner. */
  readonly duration?: number;

  /** Whether the case is still being recorded. It reads as such on the picture, and its duration goes on. */
  readonly live?: boolean;

  /**
   * The kinds of the case, each written on a badge stacked in the picture's top-left corner, keyed
   * into the declared vocabulary. At most {@link MIB_CASE_MAX_CATEGORIES} are drawn, in the order
   * they are sent.
   */
  readonly categories?: readonly string[];

  /**
   * What is true of the case right now — where it is held, what it is doing. A word is keyed into the
   * vocabulary; a {@link MibCaseFlag} is drawn as given, so its tooltip can carry live text.
   */
  readonly flags?: readonly (string | MibCaseFlag)[];

  /**
   * Whether the reader marked the case a favourite. **Left out, no star is drawn at all** — for a
   * product that has no favourites. Given, the star is drawn claimed or not, and pressing it asks the
   * page through `favouriteToggled`; the card never decides it.
   */
  readonly favourite?: boolean;

  /**
   * Whether the case can be shared. **Left out, no share button is drawn at all.** Given, the button
   * is drawn, and pressing it asks the page through `shared`; the card never shares anything itself.
   */
  readonly share?: MibCaseShare;
}

/** The share button of a case, drawn when {@link MibCase.share} is given. */
export interface MibCaseShare {
  /**
   * Why the case cannot be shared now. Given, the button is drawn disabled, presses emit nothing,
   * and this sentence is its tooltip and is read out with it.
   */
  readonly disabled?: string;
}

/** What the picture of a case that is not available here is drawn with, in place of its thumbnail. */
export interface MibCaseUnavailable extends MibThumbnailUnavailable {
  /** Why the picture is not here, written under the icon. */
  readonly label: string;
}

/** What a category is drawn with: the ramp of its badge, one of the ten of Foundations / Colors. */
export interface MibCaseCategory {
  readonly palette: MibPalette;
}

/**
 * What a flag is drawn with: an icon on a white disc, in the corner of the picture, and the meaning
 * it gives on hover. The disc never writes a word — a picture has no room for a sentence, and a
 * corner holding two or three of them has less.
 */
export interface MibCaseFlag {
  readonly icon: MibIconName;

  /** The ink of the flag, one of the seven themes. A state for a state, `ai` or `secondary` for the rest. */
  readonly theme: MibTheme;

  /**
   * What the flag means. The icon alone is drawn, so this is the only way it can be read: it is shown
   * on hover and given to a screen reader as the icon's name.
   */
  readonly tooltip: string;

  /** A state drawn small in the corner of the disc. The flag's `tooltip` says what it means. */
  readonly badge?: MibCaseFlagBadge;
}

/** The state laid on a flag: an icon on the fill of a theme, in the ink that reads on it. */
export interface MibCaseFlagBadge {
  readonly icon: MibIconName;

  /** The fill of the badge, one of the seven themes. */
  readonly theme: MibTheme;
}

/**
 * The words a product's cases can carry, declared once. The library knows none of them: a category
 * or a flag is the vocabulary of the product, so its reading belongs to the product.
 */
export interface MibCaseVocabulary {
  readonly categories?: Readonly<Record<string, MibCaseCategory>>;
  readonly flags?: Readonly<Record<string, MibCaseFlag>>;
}

/**
 * How many categories the picture takes, stacked one under the other from its top-left corner.
 *
 * Four fill the height a 16:9 still has to spare beside the duration and the controls; a fifth would
 * be drawn over the case rather than on it. A page that sends more sends the first four, so the
 * picture stays a picture — which one is dropped is the page's to decide, by the order it sends.
 */
export const MIB_CASE_MAX_CATEGORIES = 4;

/** A category nobody declared: drawn grey, never wrong and never at random. */
export const MIB_CASE_UNKNOWN_CATEGORY: MibCaseCategory = { palette: 'grey' };

export const MIB_CASE_VOCABULARY = new InjectionToken<Required<MibCaseVocabulary>>('MIB_CASE_VOCABULARY', {
  providedIn: 'root',
  factory: (): Required<MibCaseVocabulary> => ({ categories: {}, flags: {} }),
});

/**
 * Declares the categories and flags a product's cases carry, on the component hosting the cards or in
 * the root providers. Keys are matched whatever their case, so `Coronary` on a case finds `coronary`.
 *
 * ```ts
 * providers: [provideMibCaseVocabulary({
 *   categories: { coronary: { palette: 'red' }, structural: { palette: 'indigo' } },
 *   flags: { online: { icon: 'circle-check', theme: 'success', tooltip: 'Available online' } },
 * })]
 * ```
 */
export function provideMibCaseVocabulary(vocabulary: MibCaseVocabulary): Provider {
  const lower = <T>(table: Readonly<Record<string, T>> = {}): Record<string, T> => {
    const out: Record<string, T> = {};
    for (const [word, entry] of Object.entries(table)) {
      out[word.trim().toLowerCase()] = entry;
    }
    return out;
  };

  return {
    provide: MIB_CASE_VOCABULARY,
    useValue: { categories: lower(vocabulary.categories), flags: lower(vocabulary.flags) },
  };
}

/** A category as the card draws it: the label the page sent, on the ramp the vocabulary gave it. */
export interface MibCaseBadge {
  readonly label: string;
  readonly palette: MibPalette;
}

/** The ink a flag takes from its theme, on the white disc it sits in. */
export const MIB_CASE_FLAG_INK: Record<MibTheme, string> = {
  primary: 'text-mib-primary',
  secondary: 'text-gray-700',
  success: 'text-mib-success',
  warning: 'text-mib-warning-ink',
  danger: 'text-mib-danger',
  info: 'text-mib-info',
  ai: 'text-mib-branding-700',
};

/** Seconds written as the picture's corner wants them: `22:48` under an hour, `1:02:35` above. */
export function mibCaseDuration(seconds: number): string {
  return Number.isFinite(seconds) && seconds >= 0 ? durationToTime(Math.round(seconds), true) : '';
}

/** How {@link mibCaseDate} writes a moment. */
export interface MibCaseDateOptions {
  /** Adds the hour and minute after the day, joined the locale's way: `Jan 8, 2026, 5:40 PM`. */
  readonly time?: boolean;
  /** A BCP 47 tag for the date written; the browser's locale by default. */
  readonly locale?: string;
}

const MIB_CASE_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
const MIB_CASE_TIME: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

/**
 * A day written as the card's lines write it — `Aug 24, 2026` — with its time when asked; '' when unreadable.
 * Exported so every screen of a product writes the same date.
 */
export function mibCaseDate(at: MibMoment, options: MibCaseDateOptions = {}): string {
  const ms = mibMs(at);
  if (Number.isNaN(ms)) {
    return '';
  }
  const format = options.time ? { ...MIB_CASE_DAY, ...MIB_CASE_TIME } : MIB_CASE_DAY;
  return new Date(ms).toLocaleString(options.locale, format);
}
